import * as Yup from 'yup';
import { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';

// material
import { Link, Stack, Checkbox, FormControlLabel } from '@mui/material';
import { api } from '../../../Api';
// component
import { Decryption } from 'src/functions/function';
import InputField from 'src/components/InputField';
import LoadingButtonWrapper from 'src/components/LoadingButtonWrapper';
import { toast } from 'react-toastify';
import palette from 'src/theme/palette';
import OTPVerifyModal from 'src/sections/authentication/TwoFactor/OTPVerifyModal';
import Enable2FAModal from 'src/sections/authentication/TwoFactor/Enable2FAModal';

// ----------------------------------------------------------------------

export default function LoginForm() {
  const navigate = useNavigate();
  const [isSubmitting, setisSubmitting] = useState(false);
  const [isPhone, setisPhone] = useState(false);
  const [token, settoken] = useState('');

  // Modal states
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [authData, setAuthData] = useState(null);

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .required('Email is required')
      .test('valid-key', 'Invalid email', function (value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^\d{11}$/;
        if (phoneRegex.test(value)) {
          setisPhone(true);
        } else {
          setisPhone(false);
        }
        return emailRegex.test(value) || phoneRegex.test(value);
      }),
    password: Yup.string().required('Password is required')
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      remember: false
    },
    validationSchema: LoginSchema,
    onSubmit: () => {
      LoginFunction();
    }
  });

  const { errors, touched, values, handleSubmit, getFieldProps, setFieldValue } = formik;

  // Load credentials from local storage when the component mounts.
  const savedEmail = localStorage.getItem('email');
  const savedPassword = localStorage.getItem('password');
  const rememberMe = localStorage.getItem('remember') === 'true';

  useEffect(() => {
    if (savedEmail && savedPassword) {
      setFieldValue('email', savedEmail);
      setFieldValue('password', savedPassword);
      setFieldValue('remember', rememberMe);
    }
  }, [setFieldValue]);

  const LoginFunction = () => {
    setisSubmitting(true);

    const formData = new FormData();
    formData.append('email', values.email);
    formData.append('password', values.password);

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
    };

    fetch(api.login, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);

        if (status === 200 || status === 201) {
          if (result?.data?.email_verified_at) {
            // Store auth data in state but NOT in localStorage yet
            setAuthData(result);

            settoken(result.access_token);
            // Save remember me credentials if needed
            if (values.remember) {
              localStorage.setItem('email', values.email);
              localStorage.setItem('password', values.password);
              localStorage.setItem('remember', values.remember);
            } else {
              localStorage.removeItem('email');
              localStorage.removeItem('password');
              localStorage.removeItem('remember');
            }

            setisSubmitting(false);

            // Show 2FA/OTP modals based on is_auth
            if (result?.data?.is_auth == 0) {
              setShow2FAModal(true);
            } else {
              setShowOTPModal(true);
            }
          } else {
            // Email not verified - navigate to OTP verification
            localStorage.setItem('email', values.email);
            navigate('/otp-verification', {
              replace: true,
              state: { user: result?.data, token: result?.access_token, from: 'register' }
            });
            setisSubmitting(false);
          }
        } else {
          setisSubmitting(false);
          toast.error(result.error);
        }
      })
      .catch((error) => {
        console.log('error', error);
        setisSubmitting(false);
        toast.error('Login failed. Please try again.');
      });
  };

  const handleEnable2FA = () => {
    setShow2FAModal(false);
    setShowOTPModal(true);
  };

  // const handleSkip2FA = () => {
  //   setShow2FAModal(false);
  //   if (authData) {
  //     getSideBar(
  //       authData.access_token,
  //       authData.path,
  //       authData.data?.project_id,
  //       authData.data?.role?.id
  //     );
  //   }
  // };

  const handleSkip2FA = () => {
    setShow2FAModal(false);

    if (authData) {
      localStorage.setItem('token', authData.access_token);
      localStorage.setItem('user', JSON.stringify(authData.data));

      getSideBar(
        authData.access_token,
        authData.path,
        authData.data?.project_id,
        authData.data?.role?.id
      );
    }
  };

  // Handle OTP Verification
  const handleSendOTP = (otp) => {
    setisSubmitting(true);
    const formData = new FormData();
    formData.append('otp', otp);
    formData.append('email', values.email);

    const requestOptions = {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authData?.access_token}`
      },
      body: formData,
      redirect: 'follow'
    };
    fetch(api.verifyOTP, requestOptions)
      .then((response) =>
        response.text().then((result) => ({
          status: response.status,
          body: result
        }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);

        if (status === 200 || status === 201) {
          toast.success(result.message || 'OTP verified successfully!');
          setShowOTPModal(false);
          setisSubmitting(false);

          if (authData) {
            // Store token and user data ONLY after OTP verification
            localStorage.setItem('token', authData.access_token);
            localStorage.setItem('user', JSON.stringify(authData.data));
            getSideBar(
              authData.access_token,
              authData.path,
              authData.data?.project_id,
              authData.data?.role?.id
            );
          }
        } else {
          toast.error(result.message || result.error || 'Invalid OTP. Please try again.');
          setisSubmitting(false);
        }
      })
      .catch((error) => {
        console.error('OTP verification error:', error);
        toast.error('OTP verification failed. Please try again.');
        setisSubmitting(false);
      });
  };

  const handleCancelOTP = () => {
    setShowOTPModal(false);
  };

  function getSideBar(token, path, project_id, role_id) {
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);
    const queryParams = new URLSearchParams();
    role_id !== 2 && queryParams.append('project_id', project_id);

    const requestOptions = {
      method: 'GET',
      headers: myHeaders,
      redirect: 'follow'
    };

    fetch(`${api.sideBar}?${queryParams.toString()}`, requestOptions)
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          throw new Error('Failed to fetch sidebar data');
        }
      })
      .then((result) => {
        let decryptedData = Decryption(result.data);
        localStorage.setItem('permissions', JSON.stringify(decryptedData));
        setTimeout(() => {
          navigate(path, { replace: true });
        }, 1000);
      })
      .catch((error) => {
        console.log('error', error);
        toast.error('Failed to load dashboard data.');
      });
  }

  return (
    <>
      <Stack
        sx={{
          backgroundColor: '#fff',
          borderRadius: '15px',
          paddingX: 5,
          paddingY: 6,
          boxShadow: palette.primary.mainShadow,
          border: `1.5px solid ${process.env.REACT_APP_PRIMARY}`
        }}
      >
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack spacing={0}>
              <InputField
                name="email"
                label="Email"
                type="email"
                placeholder="you@example.com"
                autoComplete="username"
                {...getFieldProps('email')}
                error={touched.email && errors.email}
                helperText={touched.email && errors.email}
                labelFontMedium
                fontFamily={'poppinsRegular'}
              />

              <InputField
                name="password"
                label="Password"
                type="password"
                placeholder=""
                autoComplete="current-password"
                {...getFieldProps('password')}
                error={touched.password && errors.password}
                helperText={touched.password && errors.password}
                labelFontMedium
                fontFamily={'poppinsRegular'}
              />
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2, mt: 1 }}
            >
              <FormControlLabel
                componentsProps={{
                  typography: { fontSize: 12, color: '#18181B', fontFamily: 'poppinsRegular' }
                }}
                control={
                  <Checkbox
                    {...getFieldProps('remember')}
                    checked={values.remember}
                    sx={{
                      mx: 0.5,
                      p: 0.8,
                      '& .MuiSvgIcon-root': {
                        fontSize: 18,
                        borderRadius: 2
                      }
                    }}
                  />
                }
                label="Remember me"
              />
              <Link
                component={RouterLink}
                sx={{ fontSize: 12, fontFamily: 'poppinsMedium' }}
                to="/forgot"
                underline="hover"
              >
                Forgot password?
              </Link>
            </Stack>
            <LoadingButtonWrapper type="submit" loading={isSubmitting} title="Log in" />
          </Form>
        </FormikProvider>
      </Stack>

      {/* Enable 2FA Modal*/}
      <Enable2FAModal
        open={show2FAModal}
        onClose={() => setShow2FAModal(false)}
        onEnable={handleEnable2FA}
        onSkip={handleSkip2FA}
        loading={isSubmitting}
        token={token}
      />

      {/* OTP Verify Modal*/}
      <OTPVerifyModal
        open={showOTPModal}
        onClose={() => setShowOTPModal(false)}
        email={values.email}
        onSendOTP={handleSendOTP}
        onCancel={handleCancelOTP}
        loading={isSubmitting}
      />
    </>
  );
}
